import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header'; // Header bileşenini içe aktar
import TabBar from '../components/TabBar';
import '../styles/Taksimetre.css';
import { Container } from 'react-bootstrap';

declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const Taksimetre: React.FC = () => {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState<string>('');
  const [destination, setDestination] = useState<string>('');
  const [distance, setDistance] = useState<number | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [fare, setFare] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);
  const [isAirportRoute, setIsAirportRoute] = useState<boolean>(false);
  
  // Taksi ücret tarifesi (2025 İstanbul)
  const TAXI_RATES = {
    openingFee: 35.0,    // Açılış ücreti (TL)
    perKm: 25.0,         // Kilometre başına ücret (TL)
    minFare: 175.0,      // Minimum ücret (TL)
    waitingFee: 175.0,   // Saat başına bekleme ücreti (TL)
    airportFee: 250.0    // Havalimanı indi-bindi ücreti (TL)
  };
  
  useEffect(() => {
    // Google Maps API'sini yükle
    const loadGoogleMapsAPI = () => {
      const script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyASHeUqGfcIe2_QixdvcCjbiSVFgkQSAhQ&libraries=places&callback=initMap';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
      };
    };
    
    // Haritayı başlat
    window.initMap = () => {
      setMapLoaded(true);
      
      // İstanbul koordinatları
      const istanbul = {
        lat: 41.0082,
        lng: 28.9784
      };

      // Harita oluşturma
      const map = new window.google.maps.Map(document.getElementById("taxiMap"), {
        zoom: 11,
        center: istanbul,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });
      
      // Otomatik tamamlama için input elementlerini al
      const originInput = document.getElementById('origin') as HTMLInputElement;
      const destinationInput = document.getElementById('destination') as HTMLInputElement;
      
      // Otomatik tamamlama seçenekleri
      const options = {
        componentRestrictions: { country: 'tr' },
        fields: ['formatted_address', 'geometry', 'name'],
        strictBounds: false,
      };
      
      // Otomatik tamamlama nesnelerini oluştur
      const originAutocomplete = new window.google.maps.places.Autocomplete(originInput, options);
      const destinationAutocomplete = new window.google.maps.places.Autocomplete(destinationInput, options);
      
      // Haritaya bağla
      originAutocomplete.bindTo('bounds', map);
      destinationAutocomplete.bindTo('bounds', map);
      
      // Yer seçildiğinde input değerlerini güncelle
      originAutocomplete.addListener('place_changed', () => {
        const place = originAutocomplete.getPlace();
        if (place.formatted_address) {
          setOrigin(place.formatted_address);
        } else if (place.name) {
          setOrigin(place.name);
        }
      });
      
      destinationAutocomplete.addListener('place_changed', () => {
        const place = destinationAutocomplete.getPlace();
        if (place.formatted_address) {
          setDestination(place.formatted_address);
        } else if (place.name) {
          setDestination(place.name);
        }
      });
    };
    
    const cleanup = loadGoogleMapsAPI();
    
    return () => {
      cleanup();
      window.initMap = () => {};
    };
  }, []);
  
  // Havalimanı kontrolü
  const checkIfAirportRoute = (orig: string, dest: string): boolean => {
    const airportKeywords = ['havalimanı', 'airport', 'istanbul airport', 'sabiha gökçen', 'atatürk havalimanı'];
    const originLower = orig.toLowerCase();
    const destLower = dest.toLowerCase();
    
    return airportKeywords.some(keyword => 
      originLower.includes(keyword) || destLower.includes(keyword)
    );
  };
  
  // Mesafe hesapla
  const calculateDistance = () => {
    if (!origin || !destination) {
      setError('Lütfen başlangıç ve varış noktalarını girin.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Havalimanı rotası kontrolü
    const isAirport = checkIfAirportRoute(origin, destination);
    setIsAirportRoute(isAirport);
    
    const directionsService = new window.google.maps.DirectionsService();
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map: new window.google.maps.Map(document.getElementById("taxiMap"), {
        zoom: 11,
        center: { lat: 41.0082, lng: 28.9784 },
      })
    });
    
    directionsService.route(
      {
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: window.google.maps.TrafficModel.BEST_GUESS
        }
      },
      (response: any, status: string) => {
        setLoading(false);
        
        if (status === 'OK') {
          directionsRenderer.setDirections(response);
          
          const route = response.routes[0];
          const leg = route.legs[0];
          
          // Mesafe (km)
          const distanceInKm = leg.distance.value / 1000;
          setDistance(distanceInKm);
          
          // Süre
          setDuration(leg.duration.text);
          
          // Taksi ücreti hesapla
          const calculatedFare = calculateFare(distanceInKm, isAirport);
          setFare(calculatedFare);
        } else {
          setError('Rota hesaplanamadı. Lütfen geçerli adresler girdiğinizden emin olun.');
        }
      }
    );
  };
  
  // Taksi ücreti hesapla
  const calculateFare = (distanceInKm: number, isAirport: boolean): number => {
    // Açılış ücreti + km başına ücret
    let calculatedFare = TAXI_RATES.openingFee + (distanceInKm * TAXI_RATES.perKm);
    
    // Havalimanı ek ücreti
    if (isAirport) {
      calculatedFare += TAXI_RATES.airportFee;
    }
    
    // Minimum ücret kontrolü
    if (calculatedFare < TAXI_RATES.minFare) {
      calculatedFare = TAXI_RATES.minFare;
    }
    
    return Math.round(calculatedFare * 100) / 100; // İki ondalık basamağa yuvarla
  };
  
  // Geri dön
  const handleBack = () => {
    navigate(-1);
  };
  
  // Formu temizle
  const handleClear = () => {
    setOrigin('');
    setDestination('');
    setDistance(null);
    setDuration(null);
    setFare(null);
    setError(null);
    setIsAirportRoute(false);
    
    // Haritayı yeniden yükle
    window.initMap();
  };
  
  return (
    <div className="taksimetre-container" style={{ paddingTop: 70 }}>
      <Header title="Taksimetre Hesaplama" />
      <Container className="py-4">
        <main className="taksimetre-main">
          <div className="calculator-card">
            <div className="calculator-form">
              <div className="form-group">
                <label htmlFor="origin">
                  <i className="fas fa-map-marker-alt"></i> Başlangıç Noktası
                </label>
                <input
                  type="text"
                  id="origin"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="Nereden? (örn. İstanbul Havalimanı)"
                  disabled={!mapLoaded}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="destination">
                  <i className="fas fa-flag-checkered"></i> Varış Noktası
                </label>
                <input
                  type="text"
                  id="destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="Nereye? (örn. Taksim Meydanı)"
                  disabled={!mapLoaded}
                />
              </div>
              
              <div className="form-buttons">
                <button 
                  className="calculate-button"
                  onClick={calculateDistance}
                  disabled={loading || !mapLoaded}
                >
                  {loading ? 'Hesaplanıyor...' : 'Hesapla'}
                </button>
                
                <button 
                  className="clear-button"
                  onClick={handleClear}
                  disabled={loading || !mapLoaded}
                >
                  Temizle
                </button>
              </div>
              
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </div>
              )}
            </div>
            
            {distance !== null && fare !== null && (
              <div className="result-container">
                <div className="result-header">
                  <h3>Hesaplama Sonucu</h3>
                </div>
                
                <div className="result-details">
                  <div className="result-item">
                    <div className="result-icon">
                      <i className="fas fa-route"></i>
                    </div>
                    <div className="result-info">
                      <span className="result-label">Mesafe</span>
                      <span className="result-value">{distance.toFixed(2)} km</span>
                    </div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="result-info">
                      <span className="result-label">Tahmini Süre</span>
                      <span className="result-value">{duration}</span>
                    </div>
                  </div>
                  
                  <div className="result-item">
                    <div className="result-icon">
                      <i className="fas fa-taxi"></i>
                    </div>
                    <div className="result-info">
                      <span className="result-label">Taksi Ücreti</span>
                      <span className="result-value fare">{fare.toFixed(2)} ₺</span>
                    </div>
                  </div>
                  
                  {isAirportRoute && (
                    <div className="result-item airport-fee">
                      <div className="result-icon">
                        <i className="fas fa-plane"></i>
                      </div>
                      <div className="result-info">
                        <span className="result-label">Havalimanı Ücreti Dahil</span>
                        <span className="result-value">{TAXI_RATES.airportFee.toFixed(2)} ₺</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="result-note">
                  <p>
                    <i className="fas fa-info-circle"></i> Bu hesaplama tahmini bir değerdir. Gerçek ücret, trafik durumu, bekleme süresi ve diğer faktörlere göre değişiklik gösterebilir.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="map-container">
            <div id="taxiMap"></div>
          </div>
          
          <div className="info-card">
            <h3><i className="fas fa-info-circle"></i> Taksi Ücret Tarifesi (2025)</h3>
            <div className="tariff-details">
              <div className="tariff-item">
                <span className="tariff-label">Açılış Ücreti:</span>
                <span className="tariff-value">{TAXI_RATES.openingFee.toFixed(2)} ₺</span>
              </div>
              <div className="tariff-item">
                <span className="tariff-label">Kilometre Başına:</span>
                <span className="tariff-value">{TAXI_RATES.perKm.toFixed(2)} ₺</span>
              </div>
              <div className="tariff-item">
                <span className="tariff-label">Minimum Ücret:</span>
                <span className="tariff-value">{TAXI_RATES.minFare.toFixed(2)} ₺</span>
              </div>
              <div className="tariff-item">
                <span className="tariff-label">Havalimanı İndi-Bindi:</span>
                <span className="tariff-value">{TAXI_RATES.airportFee.toFixed(2)} ₺</span>
              </div>
              <div className="tariff-item">
                <span className="tariff-label">Bekleme Ücreti (Saat):</span>
                <span className="tariff-value">{TAXI_RATES.waitingFee.toFixed(2)} ₺</span>
              </div>
            </div>
            <p className="tariff-note">
              * Ücretler 2025 İstanbul taksi tarifesine göre hesaplanmaktadır. Havalimanı taksileri için özel tarife uygulanmaktadır.
            </p>
          </div>
        </main>
        
        <footer className="taksimetre-footer">
          <p>Bu hesaplama aracı sadece bilgi amaçlıdır. Gerçek taksi ücretleri farklılık gösterebilir.</p>
          <p><strong>İstanbul Havalimanı Taksiciler Kooperatifi</strong> - <a href="https://www.havalimanitaksi.com.tr" target="_blank" rel="noopener noreferrer">www.havalimanitaksi.com.tr</a></p>
        </footer>
      </Container>
      
      {/* Tab Bar Bileşeni */}
      <TabBar />
    </div>
  );
};

export default Taksimetre; 